/**
 * Map View for Properties page (installer/fleet only).
 * Spatial index of existing fleet data; same data source as Cards/Grid.
 * Muted Mapbox base style; one marker per property; click navigates to /app/property/:id.
 */

import 'mapbox-gl/dist/mapbox-gl.css';

import { useMemo } from 'react';
import Map, { Marker } from 'react-map-gl/mapbox';
import { PropertyMapMarkerComponent, type PropertyMapRow } from './PropertyMapMarker';
import { cn } from '@/lib/utils';

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN ?? '';
const DEFAULT_STYLE = 'mapbox://styles/mapbox/light-v11';

export interface PropertiesMapViewProps {
  /** Rows with valid latitude/longitude (already filtered). */
  rows: PropertyMapRow[];
  /** Number of properties excluded for missing location (for footnote). */
  missingLocationCount: number;
  className?: string;
}

function fitBounds(
  rows: PropertyMapRow[]
): { longitude: number; latitude: number; zoom: number } | undefined {
  if (rows.length === 0) return undefined;
  if (rows.length === 1) {
    return {
      longitude: rows[0].longitude,
      latitude: rows[0].latitude,
      zoom: 10,
    };
  }
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const r of rows) {
    minLng = Math.min(minLng, r.longitude);
    minLat = Math.min(minLat, r.latitude);
    maxLng = Math.max(maxLng, r.longitude);
    maxLat = Math.max(maxLat, r.latitude);
  }
  const centerLng = (minLng + maxLng) / 2;
  const centerLat = (minLat + maxLat) / 2;
  const spanLng = maxLng - minLng || 0.01;
  const spanLat = maxLat - minLat || 0.01;
  const zoom = Math.min(
    14,
    Math.floor(
      Math.min(
        Math.log2(360 / spanLng),
        Math.log2(180 / spanLat)
      )
    )
  );
  return { longitude: centerLng, latitude: centerLat, zoom: Math.max(2, zoom) };
}

export function PropertiesMapView({
  rows,
  missingLocationCount,
  className,
}: PropertiesMapViewProps) {
  const initialViewState = useMemo(() => fitBounds(rows), [rows]);

  if (!MAPBOX_TOKEN) {
    return (
      <div
        className={cn(
          'flex min-h-[320px] items-center justify-center rounded-lg border border-border bg-wt-bg-secondary text-wt-text-secondary',
          className
        )}
      >
        <p className="text-sm">Map unavailable. Set VITE_MAPBOX_ACCESS_TOKEN to enable map view.</p>
      </div>
    );
  }

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-wt-bg-secondary', className)}>
      <Map
        mapboxAccessToken={MAPBOX_TOKEN}
        initialViewState={
          initialViewState ?? { longitude: 0, latitude: 20, zoom: 2 }
        }
        style={{ width: '100%', height: 420 }}
        mapStyle={DEFAULT_STYLE}
        attributionControl={true}
      >
        {rows.map((row) => (
          <PropertyMapMarkerComponent key={row.id} row={row} />
        ))}
      </Map>
      {missingLocationCount > 0 && (
        <p className="border-t border-border bg-wt-bg-secondary px-3 py-2 text-xs text-muted-foreground">
          {missingLocationCount} propert{missingLocationCount === 1 ? 'y' : 'ies'} missing location
        </p>
      )}
    </div>
  );
}
